import moment from "moment/moment";

export default function DateTimeServiceProvider() {
  this.$get = ($filter) => {
    'ngInject';

    /**
     * add method 'get date in UTC' to Date object
     * @returns {Date}
     */
    Date.prototype.getUTCTime = () => {
      return new Date(
        this.getUTCFullYear(),
        this.getUTCMonth(),
        this.getUTCDate(),
        this.getUTCHours(),
        this.getUTCMinutes(),
        this.getUTCSeconds()
      );
    };

    function DateTimeService() {

      const self = this;

      /**
       * get timezone offset for current user location
       * @returns {number} mins offset
       */
      self.getLocalOffset = () => -(new Date().getTimezoneOffset()) / 60 || 0;

      /**
       * convert integer to string time
       * for example number 1550 -> string '3:30 PM'
       * @param time time in number
       * @returns {string} time
       */
      self.translateTime = time => {
        const delta = 60;
        let min = '00';
        let zzz = ' AM';
        let value = time / 100;

        if (value >= 12) {
          zzz = ' PM';
        }

        if (value % 1 !== 0) {
          min = Math.round((value % 1) * delta);
          value = value - value % 1;

          if (min === 60) {
            min = 0;
            value += 1;
          }

          if (min < 10) {
            min = '0' + min;
          }
        }

        return self.checkHour(value) + ':' + min + zzz;
      };

      /**
       * convert time to USA formatted hours
       * time range should be between 0 and 86400
       *
       * output format 1:00 PM, 2:00 AM
       *
       * @param time
       * @returns {string}
       */
      self.toHours = time => {
        let number = time / 3600;
        let timeFormat = 'AM';

        if (number >= 12) {
          timeFormat = 'PM';
          number -= 12;
        }

        if (number === 0) {
          number = '12';
        }

        return number + ' ' + timeFormat;
      };

      self.fromSecondsToHours = seconds => seconds / 3600 + ' hours';

      self.fromSecondsToMinutes = seconds => {
        let minutes = seconds / 60;
        let hours = 0;

        while (minutes >= 60) {
          minutes -= 60;
          hours++;
        }

        if (hours === 0) {
          return minutes + ' minutes';
        }

        if (minutes === 0) {
          return hours + ' hours';
        }

        return hours + ' hours ' + minutes + ' minutes';
      };

      /**
       * convert string time to number
       * for example string '3:30 PM' -> number 1550
       * @param time string
       * @returns {number}
       */
      self.untranslateTime = time => {
        let mas = time.split(' ');
        let hours = 0;

        if (mas[1] === 'PM') {
          hours += 12;
        }

        mas = mas[0].split(':');
        hours += parseInt(mas[0]);
        hours *= 100;

        if (mas[1] === '00') {
          return hours;
        } else {
          const min = parseInt(mas[1]);
          hours += min * (10 / 6);
          return hours;
        }
      };

      /**
       * join date and time to one object
       * @param date Date object
       * @param time number
       * @returns {Date}
       */
      self.joinDateAndTime = (date, time) => new Date($filter('date')(date) + ' ' + self.translateTime(time));

      /**
       * get date string by format 'yyyy-MM-dd'
       * @param date object
       * @returns string in 'yyyy-MM-dd' format
       */
      self.formatDate = date => $filter('date')(date, 'yyyy-MM-dd');

      /**
       * normalize hours to USA format
       * @param value - hours in number
       * @returns string between 0 and 12
       */
      self.checkHour = value => {
        if (value >= 12) {
          value = value - 12;
        }

        return (value === 12 || value === 0) ? '12' : value;
      };

      /**
       * get UTC time
       * @param date Date
       */
      self.getUTCTime = date => date ? new Date(date).getUTCTime() : new Date().getUTCTime();

      /**
       * add current timezone offset to date
       * @param date object
       * @returns {number} date with offset in number format
       */
      self.recalculateTime = date => {
        const hours = date.getHours();
        const localOffset = self.getLocalOffset();

        return new Date(date.setHours(hours + localOffset)).getTime();
      };

      /**
       * get time with timezone offset
       * @param offset current timezone
       * @param date Date
       * @returns Date in utc
       */
      self.getTimeByOffset = (offset, date) => {
        // create Date object for current location
        const currentDate = date ? new Date(date) : new Date();

        // get UTC time in msec
        const utc = self.getUTCTime(currentDate).getTime();

        return new Date(utc + (3600000 * offset));
      };

      /**
       * get current day number + 5 days with current timezone offset
       * @param offset
       * @returns Date
       */
      self.getLastWeekDay = offset => {
        const date = offset ? self.getTimeByOffset(offset) : new Date();
        return new Date(date.setDate(date.getDate() + 5));
      };

      /**
       * get time to complete for request
       * min time = 15 min
       * @param service
       */
      self.getTimeToComplete = service => {
        const timeToComplete = service.timeToComplete ? service.timeToComplete : 15;
        return (timeToComplete / 60) * 100;
      };

      /**
       * get start time for date
       * min date = 7AM, max date = 11PM
       * @param date
       * @param create
       * @returns {number}
       */
      self.getStartTime = (date, create) => {
        const hours = date.getHours();
        let startTime = self.untranslateTime($filter('date')(date, 'h:mm a'));

        if (create && startTime !== 700) {
          startTime += 5;
        }

        return (hours < 7 || hours >= 23 ? 700 : (startTime > 700 ? (startTime > 2300 ? startTime - 1200 : startTime) : 700));
      };

      /**
       * get end time for date
       * min date = 7AM, max date = 11PM
       * @param date
       * @returns {number}
       */
      self.getEndTime = date => {
        const hours = date.getHours();
        const endTime = self.untranslateTime($filter('date')(date, 'h:mm a'));
        return (hours < 7 || hours > 23 ? 2300 : endTime);
      };

      /**
       * return start date with current offset
       * @param date
       * @param offset
       * @returns date with offset (min date may by between 7AM and  11PM)
       */
      self.setDateByOffset = (date, offset) => {
        const currentHours = date.getHours();

        if (currentHours + offset >= 23) {
          date.setHours(7);
          date.setMinutes(0);
          date.setDate(date.getDate() + 1);
        }

        return date;
      };

      self.getNewAvailabilityDate = (offset, minRange) => {
        const timeByOffset = self.getTimeByOffset(offset, self.getTomorrow());
        let startTime = self.getStartTime(timeByOffset, true);

        if ((2300 - startTime < minRange) || timeByOffset.getHours() >= 23) {
          timeByOffset.setDate(timeByOffset.getDate() + 1);
          timeByOffset.setHours(7);
          startTime = 700;
        }

        return {
          date: timeByOffset,
          startTime: startTime,
          endTime: 2300,
          locationOffset: offset
        };
      };

      self.getEndTimeTask = (date, minTimeToComplete) => {
        const endTimeTask = new Date(date.from);
        endTimeTask.setMinutes(endTimeTask.getMinutes() + minTimeToComplete);
        endTimeTask.setSeconds(0);
        endTimeTask.setMilliseconds(0);
        return endTimeTask;
      };

      self.getTomorrow = () => {
        const date = new Date();
        return new Date(date.setDate(date.getDate() + 1));
      };

      self.month = date => moment(date).format('MMM');

      self.week = date => moment(date).format('dddd');

      self.day = date => moment(date).date();

      self.getNextDay = date => new Date(date.getTime() + 1000 * 60 * 60 * 24)

      self.getDayOfWeek = date => self.week(date).toUpperCase();

      self.year = date => moment(date).year();

    }

    return new DateTimeService();
  };
}